import { useId } from 'react';
import { ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ActionAreaFilterControl } from './ActionAreaFilterControl';
import type { ActionAreaFilter } from '../utils/actionSpatialFilter';
import {
  toggleActionFilterValue,
  type ActionFilterColumn,
  type ActionListFilters,
} from './actionListFilters';

type ActionsFilterMenuProps = {
  filters: ActionListFilters;
  onFiltersChange: (next: ActionListFilters) => void;
  columns: ActionFilterColumn[];
  areaFilter: ActionAreaFilter | null;
  onAreaFilterChange: (next: ActionAreaFilter | null) => void;
  areaSummary?: string;
  filtersActive: boolean;
  showMine?: boolean;
};

const DATE_INPUT_CLASS =
  'tw:h-8 tw:min-w-0 tw:text-xs tw:scheme-dark tw:[&::-webkit-calendar-picker-indicator]:cursor-pointer tw:[&::-webkit-calendar-picker-indicator]:opacity-100 tw:[&::-webkit-calendar-picker-indicator]:invert';

/** Filter icon popover: search, per-field values, date ranges, and drawn area. */
export function ActionsFilterMenu({
  filters,
  onFiltersChange,
  columns,
  areaFilter,
  onAreaFilterChange,
  areaSummary,
  filtersActive,
  showMine = false,
}: ActionsFilterMenuProps) {
  const idPrefix = useId();

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Filters"
            className="tw:relative"
          />
        }
      >
        <ListFilter className="tw:h-4 tw:w-4" />
        {filtersActive ? (
          <span
            className="tw:absolute tw:right-1 tw:top-1 tw:size-1.5 tw:rounded-full tw:bg-primary"
            aria-hidden
          />
        ) : null}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="dark tw:max-h-[min(36rem,80vh)] tw:w-96 tw:overflow-y-auto tw:p-3"
      >
        <div className="tw:flex tw:flex-col tw:gap-3">
          {showMine ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                id={`${idPrefix}-mine`}
                checked={filters.mine}
                onCheckedChange={(checked) =>
                  onFiltersChange({ ...filters, mine: checked === true })
                }
              />
              <Label htmlFor={`${idPrefix}-mine`} className="tw:font-normal">
                My Tasks
              </Label>
            </div>
          ) : null}
          {showMine ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                id={`${idPrefix}-needs-approval`}
                checked={filters.needsApproval}
                onCheckedChange={(checked) =>
                  onFiltersChange({
                    ...filters,
                    needsApproval: checked === true,
                  })
                }
              />
              <Label
                htmlFor={`${idPrefix}-needs-approval`}
                className="tw:font-normal"
              >
                Needs My Approval
              </Label>
            </div>
          ) : null}
          <div className="tw:space-y-1">
            <Label className="tw:text-xs tw:text-muted-foreground">
              Search
            </Label>
            <Input
              type="search"
              value={filters.search}
              onChange={(event) =>
                onFiltersChange({ ...filters, search: event.target.value })
              }
              placeholder="ID, task, description, location"
              className="tw:h-8 tw:text-xs"
            />
          </div>

          <div className="tw:grid tw:grid-cols-2 tw:gap-2">
            <div className="tw:space-y-1">
              <Label className="tw:text-xs tw:text-muted-foreground">
                Start from
              </Label>
              <Input
                type="datetime-local"
                value={filters.startFrom}
                onChange={(event) =>
                  onFiltersChange({ ...filters, startFrom: event.target.value })
                }
                className={DATE_INPUT_CLASS}
                aria-label="Start from"
              />
            </div>
            <div className="tw:space-y-1">
              <Label className="tw:text-xs tw:text-muted-foreground">
                Start to
              </Label>
              <Input
                type="datetime-local"
                value={filters.startTo}
                onChange={(event) =>
                  onFiltersChange({ ...filters, startTo: event.target.value })
                }
                className={DATE_INPUT_CLASS}
                aria-label="Start to"
              />
            </div>
            <div className="tw:space-y-1">
              <Label className="tw:text-xs tw:text-muted-foreground">
                Due from
              </Label>
              <Input
                type="datetime-local"
                value={filters.dueFrom}
                onChange={(event) =>
                  onFiltersChange({ ...filters, dueFrom: event.target.value })
                }
                className={DATE_INPUT_CLASS}
                aria-label="Due from"
              />
            </div>
            <div className="tw:space-y-1">
              <Label className="tw:text-xs tw:text-muted-foreground">
                Due to
              </Label>
              <Input
                type="datetime-local"
                value={filters.dueTo}
                onChange={(event) =>
                  onFiltersChange({ ...filters, dueTo: event.target.value })
                }
                className={DATE_INPUT_CLASS}
                aria-label="Due to"
              />
            </div>
          </div>

          {columns.map((column) => (
            <div key={column.key} className="tw:flex tw:flex-col tw:gap-2">
              <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase">
                {column.label}
              </div>
              {column.options.length === 0 ? (
                <div className="tw:text-xs tw:text-muted-foreground">
                  No values
                </div>
              ) : (
                column.options.map((option) => {
                  const optionId = `${idPrefix}-${column.key}-${option.value}`;
                  return (
                    <div
                      key={option.value}
                      className="tw:flex tw:items-center tw:gap-2"
                    >
                      <Checkbox
                        id={optionId}
                        checked={(filters.selected[column.key] ?? []).includes(
                          option.value
                        )}
                        onCheckedChange={() =>
                          onFiltersChange({
                            ...filters,
                            selected: toggleActionFilterValue(
                              filters.selected,
                              column.key,
                              option.value
                            ),
                          })
                        }
                      />
                      <Label htmlFor={optionId} className="tw:font-normal">
                        {option.label}
                      </Label>
                    </div>
                  );
                })
              )}
            </div>
          ))}

          <div className="tw:flex tw:flex-col tw:gap-2">
            <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase">
              Area
            </div>
            <ActionAreaFilterControl
              embedded
              filter={areaFilter}
              onChange={onAreaFilterChange}
              summary={areaSummary}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
